import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { hasCompleteBankDetails, normalizeBankDetails, parseBankDetails, formatSortCodeDisplay } from '../utils/bankDetails';

const styles = StyleSheet.create({
  page: { padding: 40, fontSize: 11, fontFamily: 'Helvetica', backgroundColor: '#ffffff' },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 30 },
  logo: { width: 100, height: 100, objectFit: 'contain' },
  companyInfo: { textAlign: 'right', maxWidth: '40%' },
  companyName: { fontSize: 14, fontWeight: 'bold', marginBottom: 3 },
  companyDetails: { fontSize: 9, color: '#666', marginBottom: 2 },
  invoiceTitle: { fontSize: 28, fontWeight: 'bold', color: '#667eea', marginBottom: 5 },
  invoiceNumber: { fontSize: 12, color: '#666', marginBottom: 20 },
  section: { marginBottom: 20 },
  sectionTitle: { fontSize: 10, fontWeight: 'bold', color: '#667eea', marginBottom: 8, textTransform: 'uppercase' },
  infoRow: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 15 },
  infoBlock: { width: '48%' },
  label: { fontSize: 9, color: '#888', marginBottom: 3 },
  value: { fontSize: 11, marginBottom: 2 },
  table: { marginTop: 20, marginBottom: 20 },
  tableHeader: { flexDirection: 'row', backgroundColor: '#667eea', color: '#ffffff', padding: 8, fontWeight: 'bold', fontSize: 9 },
  tableRow: { flexDirection: 'row', paddingVertical: 8, paddingHorizontal: 8, borderBottomWidth: 1, borderColor: '#f0f0f0', fontSize: 9 },
  colItem: { width: '35%' },
  colQty: { width: '8%', textAlign: 'center' },
  colPrice: { width: '17%', textAlign: 'right' },
  colDisc: { width: '12%', textAlign: 'center' },
  colVat: { width: '12%', textAlign: 'center' },
  colTotal: { width: '16%', textAlign: 'right', fontWeight: 'bold' },
  totalsSection: { marginTop: 20, alignItems: 'flex-end' },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', width: '50%', paddingVertical: 5, fontSize: 11 },
  subtotalRow: { borderBottomWidth: 1, borderColor: '#e0e0e0', marginBottom: 5 },
  grandTotalRow: { backgroundColor: '#667eea', color: '#ffffff', padding: 10, marginTop: 5, fontSize: 14, fontWeight: 'bold' },
  footer: { position: 'absolute', bottom: 30, left: 40, right: 40, textAlign: 'center', fontSize: 9, color: '#999', borderTopWidth: 1, borderColor: '#e0e0e0', paddingTop: 10 },
  paymentBox: { marginTop: 24, padding: 12, backgroundColor: '#eff6ff', borderWidth: 1, borderColor: '#bfdbfe', borderRadius: 4 },
  paymentTitle: { fontSize: 10, fontWeight: 'bold', color: '#1e3a8a', marginBottom: 6 },
  paymentLine: { fontSize: 9, color: '#1e3a8a', marginBottom: 3 },
});

const InvoicePDF: React.FC<{ invoice: any; businessSettings?: any }> = ({ invoice, businessSettings }) => {
  const { API_URL } = require('../config/api');
  const logoUrl = businessSettings?.logo?.startsWith('http') 
    ? businessSettings.logo 
    : businessSettings?.logo 
      ? `${API_URL.replace('/api','')}${businessSettings.logo}` 
      : null;

  const invoiceStatus = String(invoice.status || 'Unpaid');
  const showPayment = !['Paid', 'Cancelled', 'Void', 'Draft'].includes(invoiceStatus);
  const bank = normalizeBankDetails(parseBankDetails(businessSettings || {}), businessSettings?.name);
  const invoiceTotal = Number(invoice.total) || Number(invoice.amount) || 0;
  const balanceDue = Math.max(0, invoiceTotal - Number(invoice.paid_amount || invoice.paidAmount || 0));
  const invoiceRef = String(invoice.invoiceNumber || invoice.invoice_number || invoice.id || '');

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.headerRow}>
          <View>
            {logoUrl && <Image src={logoUrl} style={styles.logo} />}
          </View>
          <View style={styles.companyInfo}>
            {businessSettings?.name && <Text style={styles.companyName}>{businessSettings.name}</Text>}
            {businessSettings?.email && <Text style={styles.companyDetails}>{businessSettings.email}</Text>}
            {businessSettings?.phone && <Text style={styles.companyDetails}>Tel: {businessSettings.phone}</Text>}
            {businessSettings?.website && <Text style={styles.companyDetails}>{businessSettings.website}</Text>}
            {businessSettings?.companyNumber && <Text style={styles.companyDetails}>Company: {businessSettings.companyNumber}</Text>}
            {businessSettings?.vatNumber && <Text style={styles.companyDetails}>VAT: {businessSettings.vatNumber}</Text>}
          </View>
        </View>

        <Text style={styles.invoiceTitle}>INVOICE</Text>
        <Text style={styles.invoiceNumber}>Invoice #{invoice.invoiceNumber || invoice.id}</Text>

        <View style={styles.infoRow}>
          <View style={styles.infoBlock}>
            <Text style={styles.label}>INVOICE DATE</Text>
            <Text style={styles.value}>
              {invoice.invoiceDate || invoice.invoice_date
                ? new Date(invoice.invoiceDate || invoice.invoice_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                : new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
              }
            </Text>
            <Text style={styles.label}>DUE DATE</Text>
            <Text style={styles.value}>
              {(invoice.dueDate || invoice.due_date)
                ? new Date(invoice.dueDate || invoice.due_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                : 'N/A'
              }
            </Text>
            <Text style={styles.label}>STATUS</Text>
            <Text style={styles.value}>{invoice.status || 'Unpaid'}</Text>
          </View>
          <View style={styles.infoBlock}>
            <Text style={styles.label}>BILL TO</Text>
            <Text style={styles.value}>{invoice.clientName || invoice.client_name || invoice.customerName || 'N/A'}</Text>
            {(invoice.customerEmail || invoice.customer_email) && <Text style={styles.value}>{invoice.customerEmail || invoice.customer_email}</Text>}
            {(invoice.customerPhone || invoice.customer_phone) && <Text style={[styles.value, { color: '#666' }]}>{invoice.customerPhone || invoice.customer_phone}</Text>}
            {(invoice.customerAddress || invoice.customer_address) && <Text style={[styles.value, { color: '#666', fontSize: 9 }]}>{(invoice.customerAddress || invoice.customer_address || '').replace(/,/g, '\n')}</Text>}
          </View>
        </View>

        {invoice.items && invoice.items.length > 0 ? (
          <View style={styles.table}>
            <View style={styles.tableHeader}>
              <Text style={styles.colItem}>Item</Text>
              <Text style={styles.colQty}>Qty</Text>
              <Text style={styles.colPrice}>Unit Price</Text>
              <Text style={styles.colDisc}>Disc %</Text>
              <Text style={styles.colVat}>VAT %</Text>
              <Text style={styles.colTotal}>Total</Text>
            </View>
            {invoice.items.map((item: any, index: number) => {
              const qty   = Number(item.quantity) || 1;
              const price = Number(item.unitPrice || item.unit_price) || 0;
              const disc  = Number(item.discount  || item.discount_rate) || 0;
              const vat   = Number(item.vatRate   || item.vat_rate) || 0;
              const net   = qty * price * (1 - disc / 100);
              return (
                <View key={index} style={styles.tableRow}>
                  <Text style={styles.colItem}>{item.description || item.name}</Text>
                  <Text style={styles.colQty}>{qty}</Text>
                  <Text style={styles.colPrice}>£{price.toFixed(2)}</Text>
                  <Text style={styles.colDisc}>{disc}%</Text>
                  <Text style={styles.colVat}>{vat}%</Text>
                  <Text style={styles.colTotal}>£{net.toFixed(2)}</Text>
                </View>
              );
            })}
          </View>
        ) : (
          <View style={styles.section}>
            <Text style={styles.label}>DESCRIPTION</Text>
            <Text style={styles.value}>{invoice.description || 'Service provided'}</Text>
          </View>
        )}

        <View style={styles.totalsSection}>
          <View style={[styles.totalRow, styles.subtotalRow]}>
            <Text>Subtotal:</Text>
            <Text>£{(Number(invoice.subtotal) || Number(invoice.amount) || 0).toFixed(2)}</Text>
          </View>
          <View style={styles.totalRow}>
            <Text>VAT ({businessSettings?.taxRate || 0}%):</Text>
            <Text>£{(Number(invoice.total_vat) || Number(invoice.totalVat) || Number(invoice.vatAmount) || 0).toFixed(2)}</Text>
          </View>
          <View style={[styles.totalRow, styles.grandTotalRow]}>
            <Text>TOTAL:</Text>
            <Text>£{(Number(invoice.total) || Number(invoice.amount) || 0).toFixed(2)}</Text>
          </View>
        </View>

        {showPayment && hasCompleteBankDetails(bank) && (
          <View style={styles.paymentBox}>
            <Text style={styles.paymentTitle}>Payment instructions (bank transfer)</Text>
            <Text style={styles.paymentLine}>Amount due: £{(balanceDue > 0 ? balanceDue : invoiceTotal).toFixed(2)}</Text>
            <Text style={styles.paymentLine}>Account name: {bank.bank_account_name || bank.bank_name}</Text>
            <Text style={styles.paymentLine}>Sort code: {formatSortCodeDisplay(bank.bank_sort_code)}</Text>
            <Text style={styles.paymentLine}>Account number: {bank.bank_account_number}</Text>
            {invoiceRef ? <Text style={styles.paymentLine}>Reference: {invoiceRef}</Text> : null}
          </View>
        )}

        {invoice.notes && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Notes</Text>
            <Text style={styles.value}>{invoice.notes}</Text>
          </View>
        )}

        <View style={styles.footer}>
          <Text>Thank you for your business!</Text>
          {businessSettings?.email && <Text>{businessSettings.email}</Text>}
        </View>
      </Page>
    </Document>
  );
};

export default InvoicePDF;
